<template>
  <div class="driver-task-wrap">
    <div class="task-switch">
      <button
        class="switch-btn"
        :class="{ active: mode === 'PICKUP' }"
        @click="setMode('PICKUP')"
      >
        <i class="fa fa-download"></i>
        <span>Lấy hàng</span>
      </button>

      <button
        class="switch-btn"
        :class="{ active: mode === 'DELIVERY' }"
        @click="setMode('DELIVERY')"
      >
        <i class="fa fa-upload"></i>
        <span>Giao hàng</span>
      </button>
    </div>

    <div class="card classic-card">
      <div class="card-header classic-card-header">
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <div class="title">
              <i
                class="fa mr-2"
                :class="mode === 'PICKUP' ? 'fa-download' : 'fa-upload'"
              ></i>
              {{ mode === "PICKUP" ? "Xác nhận lấy hàng" : "Xác nhận giao hàng" }}
            </div>
            <div class="sub">
              {{
                mode === "PICKUP"
                  ? "Chụp ảnh phiếu/hàng hóa tại điểm lấy."
                  : "Chụp ảnh bàn giao tại điểm đến."
              }}
            </div>
          </div>
        </div>
      </div>

      <div class="card-body classic-card-body">
        <div v-if="mode === 'DELIVERY'" class="direct-row">
          <label class="direct-toggle">
            <input type="checkbox" v-model="directDelivery" />
            <span class="ml-2">Hàng đã có sẵn trên xe</span>
          </label>
          <small class="text-muted d-block mt-1 pl-4">
            Chọn nếu bạn không thực hiện bước "Lấy hàng" trên app trước đó.
          </small>
        </div>

        <div
          v-if="mode === 'DELIVERY' && directDelivery"
          class="form-group highlight-box"
        >
          <label class="label">
            Giờ xe bắt đầu chạy
            <span class="text-danger">*</span>
          </label>
          <input
            type="datetime-local"
            v-model="form.startTime"
            class="classic-input"
          />
          <small class="text-muted">
            Nhập giờ bạn bắt đầu xuất phát để tính công.
          </small>
        </div>

        <div class="form-group">
          <label class="label">
            {{ mode === "PICKUP" ? "Tên điểm lấy" : "Tên điểm giao" }}
            <span class="text-danger">*</span>
          </label>
          <input
            v-model.trim="form.locationName"
            type="text"
            class="classic-input"
            :placeholder="
              mode === 'PICKUP'
                ? 'VD: Kho Tổng - Bình Dương'
                : 'VD: Công trình A - Quận 9'
            "
          />
          <div v-if="errors.locationName" class="msg-error mt-1">
            {{ errors.locationName }}
          </div>
        </div>

        <div class="time-summary" v-if="calculatedDuration">
          <i class="fa fa-clock-o mr-1"></i>
          Thời gian chuyến đi: <strong>{{ calculatedDuration }}</strong>
        </div>

        <div class="form-group mt-3">
          <label class="label">Ghi chú (tuỳ chọn)</label>
          <textarea
            v-model.trim="form.note"
            class="classic-input"
            rows="2"
            placeholder="VD: Hàng dễ vỡ, khách nhận thay..."
          ></textarea>
        </div>

        <div class="form-group">
          <label class="label">
            Ảnh hiện trường
            <small class="text-muted font-weight-normal">
              ({{ form.photos.length }} ảnh)
            </small>
          </label>

          <div v-if="form.photos.length" class="img-grid">
            <div
              v-for="(url, idx) in form.photos"
              :key="url + idx"
              class="img-item"
            >
              <img :src="url" @click="openPreview(idx)" />
              <button
                type="button"
                class="img-remove"
                @click="removePhoto(idx)"
                :disabled="isUploading || isLoading"
              >
                &times;
              </button>
            </div>
          </div>

          <div class="btn-row">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm btn-dashed"
              @click="openCamera"
              :disabled="isUploading || isLoading"
            >
              <span v-if="isUploading" class="spinner-border spinner-border-sm mr-1"></span>
              <i v-else class="fa fa-camera mr-1"></i>
              Chụp ảnh
            </button>

            <label
              class="btn btn-outline-dark btn-sm ml-2 mb-0"
              :class="{ disabled: isUploading || isLoading }"
            >
              <i class="fa fa-folder-open mr-1"></i> Chọn ảnh
              <input
                type="file"
                accept="image/*"
                multiple
                class="d-none"
                @change="onPickFiles"
                :disabled="isUploading || isLoading"
              />
            </label>
          </div>
          <div v-if="errors.photos" class="msg-error mt-2">
            {{ errors.photos }}
          </div>
        </div>

        <div class="mt-4">
          <div v-if="error" class="alert alert-danger mb-3 py-2">
            <i class="fa fa-exclamation-triangle mr-1"></i> {{ error }}
          </div>

          <button
            class="btn btn-dark btn-block py-3"
            @click="handleSubmit()"
            :disabled="isLoading || isUploading"
          >
            <span v-if="isLoading">
              <i class="fa fa-spinner fa-spin mr-1"></i> Đang xử lý...
            </span>
            <span v-else>
              <i class="fa fa-paper-plane mr-1"></i>
              {{
                mode === "PICKUP" ? "GỬI XÁC NHẬN LẤY HÀNG" : "GỬI XÁC NHẬN GIAO HÀNG"
              }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <FullscreenCamera
      v-model="cameraOpen"
      :title="mode === 'PICKUP' ? 'Chụp ảnh lấy hàng' : 'Chụp ảnh giao hàng'"
      :busy="isUploading"
      initial-facing="environment"
      @confirm="onCameraConfirm"
      @cancel="onCameraCancel"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useDriverDashboardStore } from "@/stores/driverDashboardStore";
import fileService from "@/services/fileService";
import FullscreenCamera from "@/components/FullscreenCamera.vue";

export default {
  name: "TaiXeGiaoNhanHang",
  components: { FullscreenCamera },
  props: {
    xeId: { type: [Number, String], default: null },
    taiXeId: { type: [Number, String], default: null },
    taiXeTen: { type: String, default: "" },
    assignmentId: { type: [Number, String], default: null },
  },
  data() {
    return {
      mode: "PICKUP",
      directDelivery: false,

      form: {
        startTime: "",
        locationName: "",
        note: "",
        photos: [],
      },

      errors: { locationName: "", photos: "" },

      cameraOpen: false,
      isUploading: false,
    };
  },
  computed: {
    ...mapState(useDriverDashboardStore, ["isLoading", "error"]),

    calculatedDuration() {
      if (this.mode === "DELIVERY" && this.directDelivery && this.form.startTime) {
        const start = new Date(this.form.startTime);
        const now = new Date();
        const diffMs = now - start;
        if (diffMs > 0) return (diffMs / (1000 * 60 * 60)).toFixed(1) + " tiếng";
      }
      return null;
    },
  },
  methods: {
    ...mapActions(useDriverDashboardStore, ["submitDriverJob"]),

    setMode(m) {
      this.mode = m;
      if (m !== "DELIVERY") {
        this.directDelivery = false;
        this.form.startTime = "";
      }
      this.clearErrors();
    },

    clearErrors() {
      this.errors = { locationName: "", photos: "" };
    },

    validate() {
      this.clearErrors();
      let ok = true;

      if (!this.form.locationName?.trim()) {
        this.errors.locationName =
          this.mode === "PICKUP"
            ? "Vui lòng nhập tên điểm lấy hàng."
            : "Vui lòng nhập tên điểm giao hàng.";
        ok = false;
      }

      if (this.mode === "PICKUP" && this.form.photos.length === 0) {
        this.errors.photos = "Vui lòng chụp ít nhất 1 ảnh xác nhận.";
        ok = false;
      }

      if (this.mode === "DELIVERY" && this.directDelivery && !this.form.startTime) {
        alert("Vui lòng nhập 'Giờ xe bắt đầu chạy' để tính công.");
        ok = false;
      }

      return ok;
    },

    async handleSubmit() {
      if (!this.validate()) return;

      const payload = {
        type: this.mode,
        mode:
          this.mode === "DELIVERY" && this.directDelivery
            ? "DIRECT_DELIVERY"
            : "NORMAL",

        xeId: this.xeId,
        taiXeId: this.taiXeId,
        taiXeTen: this.taiXeTen,
        assignmentId: this.assignmentId,

        locationName: this.form.locationName,
        note: this.form.note,
        photos: this.form.photos,

        startTime:
          this.mode === "DELIVERY" && this.directDelivery
            ? this.form.startTime
            : null,
      };

      try {
        const result = await this.submitDriverJob(payload);
        alert(result?.message || "Thao tác thành công!");
        this.resetForm();
      } catch (e) {
        console.error(e);
      }
    },

    resetForm() {
      this.form = { startTime: "", locationName: "", note: "", photos: [] };
      this.directDelivery = false;
      this.clearErrors();
    },

    /* =========================
      FULLSCREEN CAMERA (giống SOS)
    ========================= */
    openCamera() {
      this.cameraOpen = true;
      document.body.style.overflow = "hidden";
    },
    onCameraCancel() {
      this.cameraOpen = false;
      document.body.style.overflow = "";
    },

    async uploadOne(file) {
      const folderName = this.mode === "PICKUP" ? "pickup" : "delivery";

      const formData = new FormData();
      formData.append("files", file, file.name);
      formData.append("folderName", folderName);

      const res = await fileService.upload(formData);
      const first = Array.isArray(res.data) ? res.data[0] : null;

      if (first?.location) return first.location;
      throw new Error("Server không trả về URL file.");
    },

    async onCameraConfirm({ blob }) {
      if (!blob || this.isUploading) return;
      this.isUploading = true;

      try {
        const rawFile = new File([blob], `job_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });

        const url = await this.uploadOne(rawFile);
        this.form.photos.push(url);
      } catch (e) {
        alert("Lỗi upload ảnh: " + (e?.message || "Không rõ"));
      } finally {
        this.isUploading = false;
        this.cameraOpen = false;
        document.body.style.overflow = "";
      }
    },

    async onPickFiles(e) {
      const files = e.target.files;
      if (!files || !files.length) return;

      this.isUploading = true;
      try {
        for (let i = 0; i < files.length; i++) {
          const url = await this.uploadOne(files[i]);
          this.form.photos.push(url);
        }
      } catch (e) {
        alert("Lỗi upload ảnh: " + (e?.message || "Không rõ"));
      } finally {
        this.isUploading = false;
        e.target.value = "";
      }
    },

    removePhoto(idx) {
      this.form.photos.splice(idx, 1);
    },

    openPreview(idx) {
      const url = this.form.photos[idx];
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    },
  },
};
</script>


<style scoped>
.driver-task-wrap {
  max-width: 720px;
  margin: 0 auto;
}

/* SWITCH BUTTONS */
.task-switch {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}
.switch-btn {
  flex: 1;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  color: #4b5563;
  transition: all 0.2s;
}
.switch-btn i {
  font-size: 18px;
}
.switch-btn.active {
  background: #111827; /* Dark theme */
  color: #fff;
  border-color: #111827;
}

/* CARD STYLES */
.classic-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  background: #fff;
  overflow: hidden;
}
.classic-card-header {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  padding: 16px;
}
.title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
}
.sub {
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
}
.classic-card-body {
  padding: 20px;
}

/* FORM ELEMENTS */
.form-group {
  margin-bottom: 16px;
}
.label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}
.classic-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  color: #111827;
  transition: border-color 0.2s;
}
.classic-input:focus {
  outline: none;
  border-color: #1f2937;
  box-shadow: 0 0 0 2px rgba(31, 41, 55, 0.1);
}

/* DIRECT DELIVERY SECTION */
.direct-row {
  background: #f3f4f6;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px dashed #d1d5db;
}
.direct-toggle {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  margin: 0;
}
.highlight-box {
  background: #fffbeb;
  border: 1px solid #fcd34d;
  padding: 12px;
  border-radius: 8px;
}

/* PREVIEW TIME */
.time-summary {
  background: #eff6ff;
  color: #1d4ed8;
  padding: 10px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  border: 1px solid #bfdbfe;
  margin-bottom: 16px;
}

/* PHOTOS GRID */
.img-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}
.img-item {
  width: 80px;
  height: 80px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}
.img-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.img-remove {
  position: absolute;
  top: 0;
  right: 0;
  background: rgba(220, 38, 38, 0.9);
  color: white;
  border: none;
  width: 20px;
  height: 20px;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
}

/* UTILS */
.btn-dashed {
  border-style: dashed;
}
.msg-error {
  color: #dc2626;
  font-size: 13px;
  font-weight: 500;
}
.btn-row {
  display: flex;
  align-items: center;
}
</style>